import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useRecurly } from '@recurly/react-recurly'
import { PayPalInstance, RecurlyError } from '@recurly/recurly-js'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { PaymentMethods } from '@pff-consumer/schema'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { SVGGroup } from '@pff-consumer/utils'
import { PayPalPaymentProps } from './paypal-payment.props'
import { Button } from '../../button/button.web'

const { useEmail } = checkoutHooks

const PAYPAL_OPTS = {
  display: { displayName: 'PFF+ Subscription' },
  braintree: { clientAuthorization: process.env.BRAINTREE_CLIENT_KEY! },
}

export const PaypalPayment: React.FC<PayPalPaymentProps> = memo((props: PayPalPaymentProps) => {
  const { onComplete, onError, onDisabledClick, isDisabled, onRecurlyTokenInit, isPaymentProcessing, size } = props
  const [paymentIsReady, setPaymentIsReady] = useState<boolean>(false)
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()
  const recurly = useRecurly()
  const payPal: PayPalInstance = recurly.PayPal(PAYPAL_OPTS)
  const onCompleteRef = useRef(onComplete)
  const email = useEmail()

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    payPal.on('token', (token: { id: string }) => {
      // the paypal token event listener can register the callback before the onComplete updates,
      // so the ref lets us use the onComplete callback when its called, not when its defined.
      onCompleteRef.current(token.id, 'paypal')
    })

    payPal.on('error', (error: RecurlyError) => {
      onError(error)
    })

    payPal.on('ready', () => {
      setPaymentIsReady(true)
    })

    payPal.on('cancel', () => {
      onRecurlyTokenInit(PaymentMethods.DEFAULT)
      sendSubscribeAnalyticsEvent('Transaction Canceled', {
        payment_method: 'paypal',
        email,
      })
    })
  }, [onError, onRecurlyTokenInit, payPal, email, sendSubscribeAnalyticsEvent])

  const launchPayPal = useCallback(() => {
    if (paymentIsReady) {
      if (!isPaymentProcessing) {
        onRecurlyTokenInit(PaymentMethods.PAYPAL)
      }
      payPal.start()
      sendSubscribeAnalyticsEvent('CTA Clicked', {
        ctaName: 'PayPal',
        ctaLocation: 'Single Page Checkout',
      })
    }
    // payPal gets recreated every cycle, and we can expect it to be updated when paymentIsReady changes.
  }, [paymentIsReady])

  return (
    <Button
      iconHeight={size === 'lg' ? 32 : 28}
      iconWidth={size === 'lg' ? 122 : 24}
      iconName={size === 'lg' ? 'paypal' : 'paypal-mini'}
      iconGroup={SVGGroup.Icons}
      isPending={isPaymentProcessing}
      onClick={isDisabled ? onDisabledClick : launchPayPal}
      variant='Paypal'
      data-analytics-id='paypal-button'
      size={size}
    />
  )
})
